import { io, type Socket } from 'socket.io-client';
import { REALTIME_PATH } from './socket-path';
import { RealtimeClientEvents } from './events';
import type { ConversationJoinPayload } from './events';

export interface RealtimeClientOptions {
  /** The API origin, e.g. `http://localhost:4501`. */
  baseUrl: string;
  /**
   * Bearer token, for clients with no cookie jar (the mobile app).
   *
   * Omitted in the browser portals: their access token is an httpOnly cookie the
   * handshake sends automatically, and it is httpOnly precisely so that script
   * cannot read it to pass here.
   *
   * Pass a function rather than a string wherever the token can rotate. A
   * string is captured once, so every automatic reconnect presents the token
   * the socket was built with — fifteen minutes later, one the gateway has
   * already retired. The function is called on every handshake.
   */
  token?: string | (() => Promise<string | null | undefined>);
}

/** The message the gateway's handshake middleware rejects with. */
export const UNAUTHORIZED_HANDSHAKE = 'unauthorized';

/**
 * Opens the portal's realtime connection.
 *
 * Framework-free on purpose — the React portals and the React Native app each
 * wrap this in their own provider, but none of them should be re-deciding the
 * transport, path, or reconnection policy. Those are properties of the server
 * this talks to, not of the app doing the talking.
 */
export function createRealtimeSocket({ baseUrl, token }: RealtimeClientOptions): Socket {
  return io(baseUrl, {
    path: REALTIME_PATH,
    // Cookies only travel on a credentialed handshake, and the portals are
    // served from a different origin than the API.
    withCredentials: true,
    ...(typeof token === 'function'
      ? {
          auth: (cb: (data: object) => void) => {
            token().then(
              (value) => cb(value ? { token: value } : {}),
              () => cb({}),
            );
          },
        }
      : token
        ? { auth: { token } }
        : {}),
    // Reconnect forever with backoff. A field officer loses signal routinely,
    // and a socket that gives up after N tries leaves the portal looking
    // connected while silently receiving nothing until a full reload.
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });
}

/**
 * Subscribes to a thread's ephemeral signals, returning the matching unsubscribe.
 *
 * Re-emitted on every reconnect by the caller: rooms live on the server side of
 * a connection, so a socket that drops and comes back has silently left every
 * conversation room it was in, and typing indicators would simply stop arriving
 * with nothing to indicate why.
 */
export function joinConversation(socket: Socket, conversationId: string): () => void {
  const payload: ConversationJoinPayload = { conversationId };
  socket.emit(RealtimeClientEvents.CONVERSATION_JOIN, payload);
  return () => socket.emit(RealtimeClientEvents.CONVERSATION_LEAVE, payload);
}

export interface ReconnectPolicyOptions {
  /**
   * Renew whatever the handshake authenticates with — rotate the session cookie,
   * or refresh the stored bearer token — before trying again. A rejection here
   * is swallowed: the next attempt simply fails the same way and backs off.
   */
  refreshCredentials(): Promise<unknown>;
  /**
   * Whether to try at all right now. False once the provider has torn the socket
   * down, or while a phone is backgrounded, so a pending retry cannot reopen a
   * socket somebody deliberately closed.
   */
  shouldReconnect(): boolean;
  /** Ceiling on the wait between attempts. */
  maxDelayMs?: number;
}

/**
 * Brings a socket back after the two disconnects socket.io never retries.
 *
 * socket.io's own reconnection covers a dropped transport, and only that. When
 * the server *refuses* the handshake — an access token that expired while the
 * laptop slept — the client gets a `connect_error` and stops for good; when the
 * server ends the connection itself the reason is `io server disconnect`, and
 * it stops for good there too. Either way the portal kept looking connected
 * while receiving nothing until a full reload.
 *
 * Both now refresh credentials and reconnect, with an exponential backoff so a
 * session that genuinely cannot be renewed does not hammer the API. Returns the
 * detach function.
 */
export function attachReconnectPolicy(socket: Socket, options: ReconnectPolicyOptions): () => void {
  const maxDelay = options.maxDelayMs ?? 30_000;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let detached = false;

  const schedule = () => {
    if (timer || detached || !options.shouldReconnect()) return;
    const delay = Math.min(1_000 * 2 ** attempt, maxDelay);
    attempt += 1;
    timer = setTimeout(() => {
      timer = undefined;
      void options
        .refreshCredentials()
        .catch(() => undefined)
        .then(() => {
          if (detached || !options.shouldReconnect() || socket.connected) return;
          socket.connect();
        });
    }, delay);
  };

  const onConnect = () => {
    attempt = 0;
  };
  // `socket.active` is false exactly when socket.io has given up on its own —
  // a middleware refusal. A transport failure leaves it true, and socket.io's
  // built-in backoff is already handling that one.
  const onConnectError = () => {
    if (!socket.active) schedule();
  };
  const onDisconnect = (reason: string) => {
    if (reason === 'io server disconnect') schedule();
  };

  socket.on('connect', onConnect);
  socket.on('connect_error', onConnectError);
  socket.on('disconnect', onDisconnect);

  return () => {
    detached = true;
    if (timer) clearTimeout(timer);
    socket.off('connect', onConnect);
    socket.off('connect_error', onConnectError);
    socket.off('disconnect', onDisconnect);
  };
}
